import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';
const API_KEY = import.meta.env.VITE_API_KEY;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    ...(API_KEY && { 'X-Api-Key': API_KEY })
  }
});

// Add token to requests if available
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('authToken');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const auth = {
  signInWithApple: (idToken) =>
    axios.post(`${API_BASE_URL || ''}/v1/auth/signInWithApple`, null, {
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
        ...(API_KEY && { 'X-Api-Key': API_KEY })
      }
    }),

  getMe: () =>
    api.get('/v1/profile'),

  refresh: (refreshToken) =>
    axios.post(`${API_BASE_URL || ''}/v1/auth/refresh`, null, {
      headers: {
        'X-Refresh-Token': refreshToken,
        ...(API_KEY && { 'X-Api-Key': API_KEY })
      }
    })
};

// Events
export const events = {
  getAll: async () => {
    const response = await api.get('/v1/events');
    return { data: response.data };
  },

  getUpcoming: async () => {
    const response = await axios.get(`${API_BASE_URL}/v1/events/upcoming`, {
      headers: API_KEY ? { 'X-Api-Key': API_KEY } : {}
    });
    return { data: response.data };
  },

  getById: async (id) => {
    const response = await api.get(`/v1/events/${id}`);
    return { data: response.data };
  },

  create: async (data) => {
    const response = await api.post('/v1/events', data);
    return { data: response.data };
  },

  update: async (id, data) => {
    const response = await api.put(`/v1/events/${id}`, data);
    return { data: response.data };
  },

  delete: async (id) => {
    const response = await api.delete(`/v1/events/${id}`);
    return response;
  },

  goLive: async (id) => {
    const response = await api.post(`/v1/events/${id}/go-live`);
    return { data: response.data };
  },

  end: async (id) => {
    const response = await api.post(`/v1/events/${id}/end`);
    return { data: response.data };
  },
};

// Streams
export const streams = {
  getAll: async () => {
    const response = await api.get('/v1/streams');
    return { data: response.data };
  },

  getById: async (id) => {
    const response = await api.get(`/v1/streams/${id}`);
    return { data: response.data };
  },

  create: async (data) => {
    const response = await api.post('/v1/streams', data);
    return { data: response.data };
  },

  update: async (id, data) => {
    const response = await api.put(`/v1/streams/${id}`, data);
    return { data: response.data };
  },

  delete: async (id) => {
    const response = await api.delete(`/v1/streams/${id}`);
    return response;
  },

  start: async (id) => {
    const response = await api.post(`/v1/streams/${id}/start`);
    return { data: response.data };
  },

  stop: async (id) => {
    const response = await api.post(`/v1/streams/${id}/stop`);
    return { data: response.data };
  },

  restart: async (id) => {
    const response = await api.post(`/v1/streams/${id}/restart`);
    return { data: response.data };
  },

  getLogs: async (id, lines = 50) => {
    const response = await api.get(`/v1/streams/${id}/logs`, { params: { lines } });
    return { data: response.data };
  },

  getStats: async (id, hours = 24) => {
    const response = await api.get(`/v1/streams/${id}/stats`, { params: { hours } });
    return { data: response.data };
  }
};

// Schedules
export const schedules = {
  getAll: async () => {
    const response = await api.get('/v1/schedules');
    return { data: response.data };
  },

  getUpcoming: async (limit = 10) => {
    const response = await api.get('/v1/schedules/upcoming', { params: { limit } });
    return { data: response.data };
  },

  getByStream: async (streamId) => {
    const response = await api.get(`/v1/schedules/stream/${streamId}`);
    return { data: response.data };
  },

  getById: async (id) => {
    const response = await api.get(`/v1/schedules/${id}`);
    return { data: response.data };
  },

  create: async (data) => {
    const response = await api.post('/v1/schedules', data);
    return { data: response.data };
  },

  update: async (id, data) => {
    const response = await api.put(`/v1/schedules/${id}`, data);
    return { data: response.data };
  },

  delete: async (id) => {
    const response = await api.delete(`/v1/schedules/${id}`);
    return response;
  }
};

// Users (admin only)
export const users = {
  getAll: async () => {
    const response = await api.get('/v1/users');
    return { data: response.data.users || response.data };
  },

  updateRole: async (id, role) => {
    const response = await api.patch(`/v1/users/${id}/role`, { role });
    return response;
  },

  delete: async (id) => {
    const response = await api.delete(`/v1/users/${id}`);
    return response;
  }
};

// Videos (authenticated)
export const videos = {
  getAll: async () => {
    const response = await api.get('/v1/videos');
    return { data: response.data };
  },

  getMyVideos: async () => {
    const response = await api.get('/v1/videos/my-videos');
    return { data: response.data };
  },

  getById: async (slug) => {
    const response = await api.get(`/v1/videos/${slug}`);
    return { data: response.data };
  },

  upload: async (formData, onProgress) => {
    const response = await api.post('/v1/videos/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(percentCompleted);
        }
      }
    });
    return response;
  },

  update: async (id, data) => {
    const formData = new FormData();

    // Add all fields to FormData
    formData.append('Title', data.title);
    if (data.remark) formData.append('Remark', data.remark);
    if (data.camera) formData.append('Camera', data.camera);
    if (data.location) formData.append('Location', data.location);
    if (data.date) formData.append('Date', data.date);
    if (data.category) formData.append('Category', data.category);
    if (data.group) formData.append('Group', data.group);
    formData.append('Fov', data.fov || 360);
    formData.append('Visibility', data.visibility);

    // Add tags as separate entries
    if (data.tags && data.tags.length > 0) {
      data.tags.forEach(tag => formData.append('Tags', tag));
    }

    // Add links if present
    if (data.links && data.links.length > 0) {
      data.links.forEach(link => formData.append('Links', JSON.stringify(link)));
    }

    const response = await api.put(`/v1/videos/${id}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response;
  },

  delete: async (slug) => {
    const response = await api.delete(`/v1/videos/${slug}`);
    return response;
  },

  // Admin endpoints
  getUnreviewed: async () => {
    const response = await api.get('/v1/videos/unreviewed');
    return { data: response.data };
  },

  review: async (slug, data) => {
    const response = await api.post(`/v1/videos/${slug}/review`, data);
    return response;
  },

  feature: async (slug) => {
    const response = await api.post(`/v1/videos/${slug}/feature`);
    return response;
  },

  unfeature: async (slug) => {
    const response = await api.delete(`/v1/videos/${slug}/feature`);
    return response;
  }
};

// Newsletter
export const newsletter = {
  subscribe: async (email, wantsTestFlightInvite) => {
    const response = await axios.post(`${API_BASE_URL}/api/newsletter/subscribe`, {
      email,
      wantsTestFlightInvite
    }, {
      headers: API_KEY ? { 'X-Api-Key': API_KEY } : {}
    });
    return response;
  },

  confirm: async (token) => {
    const response = await axios.get(`${API_BASE_URL}/api/newsletter/confirm`, {
      params: { token },
      headers: API_KEY ? { 'X-Api-Key': API_KEY } : {}
    });
    return response;
  },

  unsubscribe: async (token) => {
    const response = await axios.get(`${API_BASE_URL}/api/newsletter/unsubscribe`, {
      params: { token },
      headers: API_KEY ? { 'X-Api-Key': API_KEY } : {}
    });
    return response;
  },

  // Admin endpoints
  getSubscriptions: async () => {
    const response = await api.get('/api/newsletter/subscriptions');
    return { data: response.data.subscriptions };
  },

  getActiveSubscriptions: async () => {
    const response = await api.get('/api/newsletter/subscriptions/active');
    return { data: response.data.subscriptions };
  },

  getTestFlightSubscriptions: async () => {
    const response = await api.get('/api/newsletter/subscriptions/testflight');
    return { data: response.data.subscriptions };
  },

  exportSubscriptions: async () => {
    const response = await api.get('/api/newsletter/export', {
      responseType: 'blob'
    });
    return response;
  }
};

// Public API (no auth required)
export const publicApi = {
  getVideos: () =>
    axios.get(`${API_BASE_URL}/v1/videos`, {
      headers: API_KEY ? { 'X-Api-Key': API_KEY } : {}
    }),

  getFeaturedVideos: () =>
    axios.get(`${API_BASE_URL}/v1/videos/featured`, {
      headers: API_KEY ? { 'X-Api-Key': API_KEY } : {}
    }),

  getStreamsStatus: () =>
    axios.get(`${API_BASE_URL}/public/streams/status`, {
      headers: API_KEY ? { 'X-Api-Key': API_KEY } : {}
    }),

  getSchedule: () =>
    axios.get(`${API_BASE_URL}/public/streams/schedule`, {
      headers: API_KEY ? { 'X-Api-Key': API_KEY } : {}
    }),

  getStreamStatus: (id) =>
    axios.get(`${API_BASE_URL}/public/streams/${id}/status`, {
      headers: API_KEY ? { 'X-Api-Key': API_KEY } : {}
    })
};

export default api;
